import { useEffect, useRef } from 'react';
import type { DayButtonProps } from 'react-day-picker';

type CreateUnavailableDayButtonOptions = {
  /**
   * Resolves the human-readable unavailable reason for a date, if any. Read
   * through a stable wrapper (latest-ref) so the component identity never
   * changes — a new component type would remount the whole day grid on every
   * selection change and drop DOM focus.
   */
  getReasonText: (date: Date) => string | undefined;
};

/**
 * Day button that keeps unavailable dates clickable (so a click can surface
 * the reason helper) while exposing the reason via the hover tooltip and the
 * screen-reader label (CMS-1853 Req 4).
 *
 * Mirrors react-day-picker's default DayButton focus contract: rdp moves DOM
 * focus only by flipping the `focused` modifier and relying on the DayButton
 * to call focus() — dropping that effect breaks keyboard navigation.
 */
export function createUnavailableDayButtonComponent({
  getReasonText,
}: CreateUnavailableDayButtonOptions): (props: DayButtonProps) => JSX.Element {
  return function UnavailableDayButton({
    day,
    modifiers,
    ...buttonProps
  }: DayButtonProps) {
    const ref = useRef<HTMLButtonElement>(null);

    useEffect(() => {
      if (modifiers.focused) {
        ref.current?.focus();
      }
    }, [modifiers.focused]);

    const reasonText = modifiers.unavailable
      ? getReasonText(day.date)
      : undefined;

    const ariaLabel = reasonText
      ? `${buttonProps['aria-label'] ?? day.date.toDateString()}, ${reasonText}`
      : buttonProps['aria-label'];

    return (
      <button
        {...buttonProps}
        ref={ref}
        aria-disabled={modifiers.unavailable || undefined}
        aria-label={ariaLabel}
        title={reasonText}
        type="button"
      />
    );
  };
}
